import React from 'react';

// eslint-disable-next-line import-x/no-cycle
import { MIN_3 } from '../scenes/Scheduling/config';

/**
 * Select first part of subdomain, which is representing keycloak's realm
 * @example: tenant1.some.subdomain.myeleveo.com => tenant1
 */
const FIRST_PART_OF_SUBDOMAIN = /\.(.+)/;

const AUTH_PATH = '/auth';
const QUALITY_MANAGEMENT_PATH = '/app'; // link to Dashboard page
const USER_PROFILE_PATH = '/app/um/user-profile';
const USER_MANAGEMENT_PATH = '/app/um/users';

type EnvVars = {
  deploymentType: 'cloud' | 'onpremise';
  metrics: Partial<{
    productFruitsCode: string;
    smartlookCode: string;
  }>;
};

type MultiTenantEnv = {
  tenancy: 'multi';
};

type SingleTenantEnv = {
  tenancy: 'single';
  ssoFrontendUrl: string;
  tenant: string;
};

export type RuntimeConfiguration = {
  env?: EnvVars & (MultiTenantEnv | SingleTenantEnv);
} & React.PropsWithChildren;

const SERVICE_WORKER_NAME = 'wfm-sw.js';
const configuredNotificationDuration = Number.parseInt(process.env.REACT_APP_NOTIFICATION_DURATION || '', 10);
const websocketPath = `${process.env.REACT_APP_MOCK_API}notification/websocket`;

const defaultConfiguration = {
  accountManagementUrl: `${process.env.REACT_APP_PROXY_SERVICE_TARGET_PROTOCOL}${process.env.REACT_APP_TENANT}.${process.env.REACT_APP_PROXY_SERVICE_TARGET_HOST}${USER_PROFILE_PATH}`,
  appUrl: `${window.location.origin}${process.env.PUBLIC_URL}`,
  authUrl: `${process.env.REACT_APP_PROXY_SERVICE_TARGET_PROTOCOL}${process.env.REACT_APP_TENANT}.${process.env.REACT_APP_PROXY_SERVICE_TARGET_HOST}${AUTH_PATH}`,
  basePath: process.env.PUBLIC_URL,
  clientId: 'wfm-ui-app',
  deploymentType: 'cloud' as EnvVars['deploymentType'],
  metrics: {
    productFruitsCode: process.env.REACT_APP_METRIC_PRODUCTFRUITS_CODE,
    smartlookCode: process.env.REACT_APP_METRIC_SMARTLOOK_CODE,
  },
  qualityManagementUrl: `${process.env.REACT_APP_PROXY_SERVICE_TARGET_PROTOCOL}${process.env.REACT_APP_TENANT}.${process.env.REACT_APP_PROXY_SERVICE_TARGET_HOST}${QUALITY_MANAGEMENT_PATH}`,
  restPath: `${process.env.REACT_APP_MOCK_API}`,
  wsPath: websocketPath,
  wsUrl: `ws://${window.location.host}${websocketPath}`,
  tenant: `${process.env.REACT_APP_TENANT}`,
  userManagementUrl: `${process.env.REACT_APP_PROXY_SERVICE_TARGET_PROTOCOL}${process.env.REACT_APP_TENANT}.${process.env.REACT_APP_PROXY_SERVICE_TARGET_HOST}${USER_MANAGEMENT_PATH}`,
  notificationDuration: Number.isNaN(configuredNotificationDuration) ? MIN_3 : configuredNotificationDuration * 1000,
  serviceWorkerUrl: `${window.location.origin}${process.env.PUBLIC_URL}/${SERVICE_WORKER_NAME}`,
};

const ConfigurationContext = React.createContext(defaultConfiguration);

export const ConfigurationProvider: React.FC<RuntimeConfiguration> = ({ children, env }) => {
  const { origin, host } = window.location;
  /* eslint-disable prefer-const */
  let {
    accountManagementUrl,
    tenant,
    authUrl,
    deploymentType,
    metrics,
    userManagementUrl,
    wsUrl,
    wsPath,
    qualityManagementUrl,
  } = defaultConfiguration;
  /* eslint-enable prefer-const */

  if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'test') {
    [tenant] = env?.tenancy === 'multi' ? host.split(FIRST_PART_OF_SUBDOMAIN) : [env?.tenant ?? ''];
    authUrl = env?.tenancy === 'single' ? env.ssoFrontendUrl : `${origin}${AUTH_PATH}`;
    qualityManagementUrl = `${origin}${QUALITY_MANAGEMENT_PATH}`;
    accountManagementUrl = `${window.location.origin}${USER_PROFILE_PATH}`;
    userManagementUrl = `${window.location.origin}${USER_MANAGEMENT_PATH}`;

    wsUrl = `wss://${host}${wsPath}`;
    deploymentType = env?.deploymentType ?? deploymentType;
    metrics.productFruitsCode = env?.metrics.productFruitsCode;
    metrics.smartlookCode = env?.metrics.smartlookCode;
  } else if (process.env.REACT_APP_TENANCY === 'single' && tenant === 'default') {
    // when running app locally against single-tenant VM, auth URL should avoid tenant's subpart
    authUrl = `${process.env.REACT_APP_PROXY_SERVICE_TARGET_PROTOCOL}${process.env.REACT_APP_PROXY_SERVICE_TARGET_HOST}${AUTH_PATH}`;
  }

  const value = {
    ...defaultConfiguration,
    accountManagementUrl,
    authUrl,
    deploymentType,
    metrics,
    tenant,
    qualityManagementUrl,
    userManagementUrl,
    wsUrl,
  };

  return <ConfigurationContext.Provider value={value}>{children}</ConfigurationContext.Provider>;
};

export const useConfig = () => React.useContext(ConfigurationContext);
